import {HttpClient, HttpHeaders} from '@angular/common/http';
import {inject, Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {Version} from '../interfaces/version';
import {RuntimeConfigService} from './runtime-config.service';

@Injectable({
  providedIn: 'root'
})
export class VersionService {
  protected http = inject(HttpClient);
  private readonly _versions: Map<string, string> = new Map<string, string>();

  protected constructor(private readonly runtimeConfig: RuntimeConfigService) {
  }

  public initVersions() {
    if (this.runtimeConfig.envName === 'test' || this.runtimeConfig.envName === 'dev') {
      this.getCoreServiceVersion().subscribe({
        next: (version) => {
          this._versions.set("core-service", version.version);
        }, error: err => {
          console.log('No version for core-service available: ' + err);
        }
      })
      this.getCoreServiceMigrationVersion().subscribe({
        next: (version) => {
          this._versions.set("core-service-db-migration", version.version);
        }, error: err => {
          console.log('No version for core-service-db-migration available: ' + err);
        }
      })
      this.getHandlingMigrationVersion().subscribe({
        next: (version) => {
          this._versions.set("gch-transaction-handling-db-migration", version.version);
        }, error: err => {
          console.log('No version for transaction-handling-db-migration available: ' + err);
        }
      })
      this.getOpensearchMigrationVersion().subscribe({
        next: (version) => {
          this._versions.set("gch-opensearch-migration", version.version);
        }, error: err => {
          console.log('No version for opensearch-migration available: ' + err);
        }
      })
      this.getTransactionHandlingVersion().subscribe({
        next: (version) => {
          this._versions.set("gch-transaction-handling", version.version);
        }, error: err => {
          console.log('No version for transaction-handling available: ' + err);
        }
      })
      this.getTransactionSearchVersion().subscribe({
        next: (version) => {
          this._versions.set("gch-transaction-search", version.version);
        }, error: err => {
          console.log('No version for transaction-search available: ' + err);
        }
      })
      this.getSelfVersion().subscribe({
        next: (version) => {
          this._versions.set("girocheckout-client-ui", version.version);
        }, error: err => {
          console.log('No version for girocheckout-ui available: ' + err);
        }
      })
      this.getEpaVersion().subscribe({
        next: (version) => {
          this._versions.set("gch-epa-distribution", version.version);
        }, error: err => {
          console.log('No version for epa-distribution available: ' + err);
        }
      })
      this.getEpaMigrationVersion().subscribe({
        next: (version) => {
          this._versions.set("gch-epa-distribution-db-migration", version.version);
        }, error: err => {
          console.log('No version for epa-distribution-db-migration available: ' + err);
        }
      })
      this.getApiForwardVersion().subscribe({
        next: (version) => {
          this._versions.set("gch-api-forward", version.version);
        }, error: err => {
          console.log('No version for api-forward available: ' + err);
        }
      })
      this.getPayPageVersion().subscribe({
        next: (version) => {
          this._versions.set("gch-paymentpage", version.version);
        }, error: err => {
          console.log('No version for pay-page available: ' + err);
        }
      })
    }
  }

  public get versions(): Map<string, string> {
    return this._versions;
  }

  public get formattedVersions(): string {
    let formattedVersions = ''
    this._versions.forEach((k, v) => {
      formattedVersions = formattedVersions + v + ' : ' + k + '\n';
    })
    return formattedVersions;
  }

  private getCoreServiceVersion(): Observable<Version> {
    return this.getVersion(this.runtimeConfig.dataBackend + '/version');
  }

  private getCoreServiceMigrationVersion(): Observable<Version> {
    return this.getVersion(this.runtimeConfig.dataBackend + '/version/migration');
  }

  private getHandlingMigrationVersion(): Observable<Version> {
    return this.getVersion(this.runtimeConfig.dataBackend + '/version/handling/migration');
  }

  private getOpensearchMigrationVersion(): Observable<Version> {
    return this.getVersion(this.runtimeConfig.dataBackend + '/version/opensearch/migration');
  }

  private getTransactionHandlingVersion(): Observable<Version> {
    return this.getVersion(this.runtimeConfig.transactionHandlingBackend + '/version');
  }

  private getTransactionSearchVersion(): Observable<Version> {
    return this.getVersion(this.runtimeConfig.transactionSearchBackend + '/version');
  }

  private getSelfVersion(): Observable<Version> {
    return this.getVersion("" + '/version');
  }

  private getEpaVersion(): Observable<Version> {
    return this.getVersion(this.runtimeConfig.epaDistributionBackend + '/version');
  }

  private getEpaMigrationVersion(): Observable<Version> {
    return this.getVersion(this.runtimeConfig.epaDistributionBackend + '/version/migration');
  }

  private getApiForwardVersion(): Observable<Version> {
    return this.getVersion(this.runtimeConfig.apiForwardBackend + '/version');
  }

  private getPayPageVersion(): Observable<Version> {
    return this.getVersion(this.runtimeConfig.payPageBackend + '/version');
  }

  private getVersion(url: string): Observable<Version> {
    let headers = new HttpHeaders().set('content-type', 'application/json; charset=utf-8');
    return this.http.get<Version>(url, {headers: headers});
  }
}
